/**
 * ContractDetailSheet - Sheet lateral para visualizar detalhes de um contrato
 * Mostra status, signatários, timeline de eventos e preview do HTML
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-09 v1.2.0 - Status do contrato e dos signatários passam a usar
 *            bolinha colorida + rótulo em cor padrão (mesmo padrão da listagem
 *            ContractsTableView), substituindo os badges coloridos.
 *   RELEASE: 2026-02-16 v1.1.0 - Timeline de eventos do contrato
 */

import React, { useState, useEffect } from 'react';
import { ContractActionBar } from './ContractActionBar';
import { AddSignerDialog } from './AddSignerDialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileSignature, User, Clock, CheckCircle2, XCircle, AlertCircle, History, RotateCcw, Loader2, Eye, UserPlus, Trash2, Copy, Check, Car, Pencil } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import { CONTRACT_STATUS_LABELS, SIGNATURE_STATUS_LABELS } from '@/types/contracts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { ContractTimeline } from './ContractTimeline';
import { ContractTemplatePreview } from './ContractTemplatePreview';
import { ContractPdfPagePreview } from './ContractPdfPagePreview';
import { useContractAssetUrl } from '@/hooks/useContractAssetUrl';
import { useContractActions } from '@/hooks/useContractActions';
// v2026-08: veículos congelados no contrato (snapshot)
import { useContractVehicles } from '@/hooks/useCustomerVehicles';
// Flag `vehicles_enabled` do tenant (esconde o bloco de veículos quando desligado)
import { useVehiclesModuleEnabled } from '@/hooks/useVehiclesModule';

import { Button } from '@/components/ui/button';
import { showToast } from '@/lib/toast';
import type { ContractStatus, SignatureStatus } from '@/types/contracts';

// Props do componente
interface ContractDetailSheetProps {
  contractId: string | null;
  onClose: () => void;
  /** v1.2.0: Permissão de edição (gerar PDF, enviar/reenviar, signatários, status) */
  canEdit?: boolean;
  /** v1.2.0: Permissão de exclusão (Excluir Rascunho, Cancelar contrato) */
  canDelete?: boolean;
}

/** Ícones por status de assinatura */
const signatureStatusIcons: Record<SignatureStatus, typeof CheckCircle2> = {
  pending: AlertCircle,
  signed: CheckCircle2,
  refused: XCircle,
  expired: Clock,
};

/**
 * v1.2.0 — Cor da bolinha por status do contrato
 * Mesmo mapeamento usado em ContractsTableView (listagem)
 */
function getContractStatusDotColor(status: string): string {
  switch (status) {
    case 'signed': return 'bg-green-500';
    case 'sent': return 'bg-amber-500';
    case 'generated': return 'bg-blue-500';
    case 'cancelled': return 'bg-destructive';
    case 'error':
    case 'expired': return 'bg-red-500';
    default: return 'bg-muted-foreground/40'; // draft e demais
  }
}

/** v1.2.0 — Cor da bolinha por status do signatário */
function getSignatureStatusDotColor(status: string): string {
  switch (status) {
    case 'signed': return 'bg-green-500';
    case 'pending': return 'bg-amber-500';
    case 'refused': return 'bg-red-500';
    default: return 'bg-muted-foreground/40'; // expired e demais
  }
}


/** Subcomponente: lista de signatários com botões de reenvio e remoção */
function SignersList({ signers, contractId, tenantId, contractStatus, canEdit, allowedDeliveryMethods }: {
  signers: Array<{
    id: string;
    signer_name: string;
    signer_email: string | null;
    signer_phone: string | null;
    signer_role: string;
    status: SignatureStatus;
    signed_at: string | null;
    sign_order: number;
    provider_data: Record<string, unknown> | null;
  }>;
  contractId: string;
  tenantId: string;
  contractStatus: ContractStatus;
  /** v1.2.0: Permissão de edição (controla botão de remover signatário) */
  canEdit?: boolean;
  /** Métodos de entrega permitidos pelo PublishedContract (usados na edição) */
  allowedDeliveryMethods?: ('email' | 'whatsapp' | 'sms')[];
}) {
  const { resendSignature, removeSigner, updateSigner } = useContractActions(contractId, tenantId);
  /** ID do signatário em edição (abre o formulário em modo edição) */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  /** Estado para feedback visual do botão copiar link */
  const [copiedSignerId, setCopiedSignerId] = useState<string | null>(null);

  /** Permite adicionar/remover signatários nos status generated e sent + permissão de edição */
  const canModifySigners = ['generated', 'sent'].includes(contractStatus) && (canEdit ?? true);

  const handleResend = async (sigId: string) => {
    setResendingId(sigId);
    try {
      await resendSignature.mutateAsync(sigId);
    } finally {
      setResendingId(null);
    }
  };

  /** Remove signatário após confirmação */
  const handleRemove = async (sigId: string) => {
    setRemovingId(sigId);
    try {
      await removeSigner.mutateAsync(sigId);
    } finally {
      setRemovingId(null);
      setConfirmRemoveId(null);
    }
  };

  /** Signatário que será removido (para exibir nome no AlertDialog) */
  const signerToRemove = signers.find(s => s.id === confirmRemoveId);

  /** Signatário em edição — alimenta o formulário em modo edição */
  const signerToEdit = signers.find(s => s.id === editingId);

  return (
    <>
      <div className="space-y-2">
        {signers
          .sort((a, b) => a.sign_order - b.sign_order)
          .map((sig) => {
            const StatusIcon = signatureStatusIcons[sig.status] || AlertCircle;
            return (
              <div key={sig.id} className="flex items-center gap-3 p-2 rounded-lg border">
                <StatusIcon className={cn('h-4 w-4 shrink-0', {
                  'text-amber-500': sig.status === 'pending',
                  'text-green-500': sig.status === 'signed',
                  'text-red-500': sig.status === 'refused',
                  'text-muted-foreground': sig.status === 'expired',
                })} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{sig.signer_name}</p>
                  <p className="text-xs text-muted-foreground">{sig.signer_role}</p>
                  {/* Email e telefone do signatário */}
                  {sig.signer_email && (
                    <p className="text-[11px] text-muted-foreground/70 truncate">
                      {sig.signer_email}
                    </p>
                  )}
                  {sig.signer_phone && (
                    <p className="text-[11px] text-muted-foreground/70 truncate">
                      {formatPhoneForDisplay(sig.signer_phone)}
                    </p>
                  )}
                </div>
                {/* Badge de status + botões de ação */}
                <div className="flex items-center gap-1.5 shrink-0">
                  {/* v1.2.0: bolinha colorida + rótulo em cor padrão */}
                  <span className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'h-2 w-2 shrink-0 rounded-full',
                        getSignatureStatusDotColor(sig.status),
                      )}
                    />
                    <span className="text-[11px] text-foreground">
                      {SIGNATURE_STATUS_LABELS[sig.status]?.label || sig.status}
                    </span>
                  </span>

                  {sig.signed_at && (
                    <span className="text-[10px] text-muted-foreground">
                      {format(new Date(sig.signed_at), 'dd/MM HH:mm')}
                    </span>
                  )}
                  {/* Botão copiar link - apenas para pendentes em contratos enviados */}
                  {sig.status === 'pending' && contractStatus === 'sent' && (() => {
                    const providerData = sig.provider_data as Record<string, unknown> | null;
                    const signLink = providerData?.sign_link as string | undefined;
                    return (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={!signLink}
                        onClick={() => {
                          if (signLink) {
                            navigator.clipboard.writeText(signLink);
                            setCopiedSignerId(sig.id);
                            showToast.success('Link de assinatura copiado!');
                            setTimeout(() => setCopiedSignerId(null), 2000);
                          }
                        }}
                        title={signLink ? 'Copiar link de assinatura' : 'Link ainda não disponível'}
                      >
                        {copiedSignerId === sig.id ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    );
                  })()}
                  {/* Botão reenviar individual - apenas para pendentes em contratos enviados */}
                  {sig.status === 'pending' && contractStatus === 'sent' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleResend(sig.id)}
                      disabled={resendingId === sig.id}
                      title="Reenviar link de assinatura"
                    >
                      {resendingId === sig.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <RotateCcw className="h-3 w-3" />
                      )}
                    </Button>
                  )}
                  {/* Botão editar - corrige dados de signatário pendente */}
                  {sig.status === 'pending' && canModifySigners && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setEditingId(sig.id)}
                      title="Editar dados do signatário"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                  {/* Botão remover - apenas para pendentes em contratos generated/sent */}
                  {sig.status === 'pending' && canModifySigners && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => setConfirmRemoveId(sig.id)}
                      disabled={removingId === sig.id}
                      title="Remover signatário"
                    >
                      {removingId === sig.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {/* AlertDialog de confirmação para remoção */}
      <AlertDialog open={!!confirmRemoveId} onOpenChange={(open) => { if (!open) setConfirmRemoveId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover signatário?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover <strong>{signerToRemove?.signer_name}</strong> deste contrato?
              {contractStatus === 'sent' && ' O signatário também será removido no provider de assinatura.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRemoveId && handleRemove(confirmRemoveId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Formulário em modo edição do signatário pendente */}
      <AddSignerDialog
        open={!!editingId}
        onOpenChange={(open) => { if (!open) setEditingId(null); }}
        mode="edit"
        willReplaceOnProvider={contractStatus === 'sent'}
        allowedDeliveryMethods={allowedDeliveryMethods}
        isLoading={updateSigner.isPending}
        initialData={signerToEdit ? {
          signer_name: signerToEdit.signer_name,
          signer_email: signerToEdit.signer_email,
          signer_phone: signerToEdit.signer_phone,
          signer_role: signerToEdit.signer_role,
          delivery_method: (signerToEdit.provider_data as Record<string, unknown> | null)?.delivery_method as string | undefined,
        } : null}
        onSubmit={async (data) => {
          if (!editingId) return;
          await updateSigner.mutateAsync({ signature_id: editingId, ...data });
          setEditingId(null);
        }}
      />
    </>
  );
}

/** Wrapper para AddSignerDialog que pode usar hooks de forma segura */
function AddSignerDialogWrapper({ open, onOpenChange, contractId, tenantId, allowedDeliveryMethods }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: string;
  tenantId: string;
  /** Métodos de entrega permitidos pelo PublishedContract associado ao contrato */
  allowedDeliveryMethods?: ('email' | 'whatsapp' | 'sms')[];
}) {
  const { addSigner } = useContractActions(contractId, tenantId);

  return (
    <AddSignerDialog
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={async (signerData) => {
        await addSigner.mutateAsync(signerData);
      }}
      isLoading={addSigner.isPending}
      allowedDeliveryMethods={allowedDeliveryMethods}
    />
  );
}

/**
 * Sheet de detalhes de um contrato
 */
export function ContractDetailSheet({ contractId, onClose, canEdit = true, canDelete = true }: ContractDetailSheetProps) {
  /** Estado para controlar o modal de preview do contrato */
  const [showPreview, setShowPreview] = useState(false);
  /** Estado para controlar o dialog de adicionar signatário */
  const [showAddSigner, setShowAddSigner] = useState(false);
  /** URL resolvida da imagem de fundo do template (signed URL ou URL pública) */
  const [backgroundPreviewUrl, setBackgroundPreviewUrl] = useState<string | null>(null);
  // Busca dados completos do contrato
  // Status de processamento assíncrono que requerem polling fallback
  const processingStatuses = ['queued_pdf', 'processing_pdf', 'queued_signature', 'processing_signature'];

  /** Rastreia se o contrato está em processamento para ativar polling */
  const [isProcessingStatus, setIsProcessingStatus] = useState(false);

  // QueryClient declarado antes do useQuery para uso dentro do queryFn
  const queryClient = useQueryClient();

  const { data: contract, isLoading } = useQuery({
    queryKey: ['contract-detail', contractId],
    queryFn: async () => {
      if (!contractId) return null;

      // RELEASE v2.0.0 - 2026-05-10: server-side via RPC get_contract_detail
      // Substitui SELECT direto + joins aninhados; tenant validado no servidor
      const { data, error } = await supabase.rpc('get_contract_detail', {
        p_contract_id: contractId,
      });

      if (error) throw error;
      const row = data as any;

      // Atualiza flag de processamento baseado no status retornado
      const isProcessing = !!row?.status && processingStatuses.includes(row.status as string);
      setIsProcessingStatus(isProcessing);

      // Invalida a timeline sempre que o queryFn executa (polling ou manual)
      queryClient.invalidateQueries({ queryKey: ['contract-timeline', contractId] });

      return row;
    },
    enabled: !!contractId,
    // Polling fallback de 5s durante estados de processamento
    // Garante atualização mesmo se Realtime falhar
    refetchInterval: isProcessingStatus ? 5000 : false,
    // staleTime: 0 garante dados frescos ao reabrir o sheet em < 5 minutos.
    // Seguro duplo junto com o staleTime: 0 do useContractsByDeal:
    // se o sheet foi fechado durante uma assinatura e reaberto logo após,
    // os dados já virão atualizados sem depender dos listeners Realtime.
    staleTime: 0,
  });

  // Resolve URL do PDF final do contrato (para preview quando modelo é 'uploaded_pdf'
  // ou quando não há conteúdo HTML). Suporta cross-bucket (anexos/contratos).
  const contractPdfUrl = useContractAssetUrl((contract as any)?.pdf_storage_path ?? null);

  // =========================================================================
  // Resolve URL de background do template para o preview do contrato
  // Suporta URLs públicas diretas e paths legados do bucket 'contratos'
  // =========================================================================

  useEffect(() => {
    const bgPath = (contract as any)?.contract_templates?.background_image_path;
    if (!bgPath) {
      setBackgroundPreviewUrl(null);
      return;
    }
    // URL pública direta (já resolvida)
    if (bgPath.startsWith('http')) {
      setBackgroundPreviewUrl(bgPath);
    } else {
      // Path legado: gera URL assinada com validade de 1h
      supabase.storage.from('contratos').createSignedUrl(bgPath, 3600)
        .then(({ data }) => {
          if (data?.signedUrl) setBackgroundPreviewUrl(data.signedUrl);
        });
    }
  }, [contract]);

  // =========================================================================
  // Realtime: escuta mudanças no contrato e signatários via Supabase Realtime
  // Substitui o polling de 5s por subscription instantânea
  // =========================================================================

  useEffect(() => {
    if (!contractId) return;

    const logger = createLogger('ContractDetailSheet:Realtime');

    const channel = supabase
      .channel(`contract-status-${contractId}`)
      // Escuta mudanças na tabela contracts filtrada pelo id do contrato aberto
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contracts',
          filter: `id=eq.${contractId}`,
        },
        (payload) => {
          const newData = payload.new as Record<string, unknown>;
          logger.info('Evento Realtime recebido para contrato', { status: newData.status });

          // Merge otimista: atualiza cache do detalhe preservando joins
          // Realtime retorna apenas colunas escalares — joins devem ser preservados manualmente
          queryClient.setQueryData(
            ['contract-detail', contractId],
            (old: any) => old ? {
              ...old,
              ...newData,
              // Preserva joins que não vêm no payload Realtime
              contract_templates: old.contract_templates,
              contract_signatures: old.contract_signatures,
              contract_provider_tracking: old.contract_provider_tracking,
            } : old
          );

          // v1.1.0: Sincroniza flag de processamento imediatamente via Realtime
          // Evita lag de até 5s para o polling detectar que o processamento terminou
          setIsProcessingStatus(processingStatuses.includes(newData.status as string));

          // v1.2.0: Propaga atualização para o cache da lista quando o sheet está aberto
          // Garante que lista e modal fiquem sempre em sincronia com o mesmo evento Realtime
          // Preserva joins (contract_templates, contract_signatures) que não vêm no payload
          const dealIdFromPayload = newData.deal_id as string;
          if (dealIdFromPayload) {
            queryClient.setQueryData(
              ['contracts', 'deal', dealIdFromPayload],
              (old: any[] | undefined) =>
                old?.map(c => {
                  if (c.id !== contractId) return c;
                  return {
                    ...c,
                    ...newData,
                    contract_templates: c.contract_templates,   // preserva join
                    contract_signatures: c.contract_signatures, // preserva join
                  };
                })
            );
          }

          // Para estados finais, refetch completo para garantir dados de joins atualizados
          const finalStatuses = ['generated', 'sent', 'signed', 'error', 'cancelled'];
          if (finalStatuses.includes(newData.status as string)) {
            queryClient.invalidateQueries({
              queryKey: ['contract-detail', contractId],
            });
          }

          // Invalida timeline para refletir mudanças de status em tempo real
          queryClient.invalidateQueries({
            queryKey: ['contract-timeline', contractId],
          });
        }
      )
      // Escuta mudanças nos signatários do contrato (assinatura, recusa, etc.)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contract_signatures',
          filter: `contract_id=eq.${contractId}`,
        },
        () => {
          // Signatários têm estrutura complexa com provider_data, invalidar é mais seguro
          logger.info('Evento Realtime recebido para signatário do contrato');
          queryClient.invalidateQueries({
            queryKey: ['contract-detail', contractId],
          });
          // Também invalidar timeline pois assinatura gera evento no histórico
          queryClient.invalidateQueries({
            queryKey: ['contract-timeline', contractId],
          });
        }
      )
      // Escuta novos registros no histórico de status (inseridos por webhooks/triggers)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'contract_status_history',
          filter: `contract_id=eq.${contractId}`,
        },
        () => {
          logger.info('Evento Realtime recebido para histórico de status do contrato');
          queryClient.invalidateQueries({
            queryKey: ['contract-timeline', contractId],
          });
          // Atualizar detalhe do contrato pois status pode ter mudado
          queryClient.invalidateQueries({
            queryKey: ['contract-detail', contractId],
          });
        }
      )
      // v1.3.0: Escuta atualizações do tracking (external_url do PDF assinado)
      // Disparado quando webhook salva a URL do documento assinado na tabela
      // contract_provider_tracking (agora incluída na publicação supabase_realtime)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contract_provider_tracking',
          filter: `contract_id=eq.${contractId}`,
        },
        (payload) => {
          const newTracking = payload.new as Record<string, unknown>;
          logger.info('Evento Realtime recebido para tracking do contrato', {
            hasExternalUrl: !!newTracking.external_url,
          });
          // Atualiza apenas o tracking no cache preservando o restante do contrato
          queryClient.setQueryData(
            ['contract-detail', contractId],
            (old: any) => old ? {
              ...old,
              // Wrapping em array para manter o mesmo formato retornado pelo Supabase (PostgREST)
              // Relações one-to-many são SEMPRE retornadas como array; salvar como objeto
              // causava inconsistência quando o refetch subsequente sobrescrevia com array.
              contract_provider_tracking: [newTracking],
            } : old
          );
          // Invalida para garantir consistência total (botão "Ver PDF Assinado")
          queryClient.invalidateQueries({
            queryKey: ['contract-detail', contractId],
          });
        }
      )
      .subscribe((status) => {
        logger.info('Status da subscription Realtime', { status });
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contractId, queryClient]);

  const statusInfo = contract
    ? CONTRACT_STATUS_LABELS[(contract.status as ContractStatus)] || CONTRACT_STATUS_LABELS.draft
    : null;

  return (
    <Sheet open={!!contractId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="!w-full sm:!w-[480px] sm:!max-w-[480px] p-0 overflow-x-hidden">
        {isLoading ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : contract ? (
          <ScrollArea className="h-full">
            <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
              {/* Header */}
              <SheetHeader className="space-y-2">
                <div className="flex items-center gap-2">
                  <FileSignature className="h-5 w-5 text-primary" />
                  {/* Número sequencial do contrato */}
                  {contract.contract_number && (
                    <span className="text-xs text-muted-foreground font-mono">
                      #{contract.contract_number}
                    </span>
                  )}
                  <SheetTitle className="text-base">{contract.title}</SheetTitle>
                </div>
                {statusInfo && (
                  /* v1.2.0: bolinha colorida + rótulo em cor padrão */
                  <div className="flex w-fit items-center gap-1.5">
                    <span
                      className={cn(
                        'h-2 w-2 shrink-0 rounded-full',
                        getContractStatusDotColor(contract.status as string),
                      )}
                    />
                    <span className="text-xs text-foreground">{statusInfo.label}</span>
                  </div>
                )}

              </SheetHeader>

              {/* Barra de ações contextuais */}
              <ContractActionBar
                contractId={contract.id}
                tenantId={contract.tenant_id}
                status={contract.status as ContractStatus}
                pdfStoragePath={contract.pdf_storage_path}
                externalUrl={(contract as any).contract_provider_tracking?.[0]?.external_url}
                queuedAt={(contract as any).queued_at}
                sentAt={(contract as any).sent_at}
                signedAt={(contract as any).signed_at}
                queueError={(contract as any).queue_error}
                onDeleteSuccess={onClose}
                canEdit={canEdit}
                canDelete={canDelete}
              />

              {/* Informações gerais */}
              <div className="space-y-2 text-sm">
                {contract.contract_templates?.name && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Modelo</span>
                    <span className="font-medium">{contract.contract_templates.name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Criado em</span>
                  <span>{format(new Date(contract.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
                </div>
                {contract.sent_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Enviado em</span>
                    <span>{format(new Date(contract.sent_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
                  </div>
                )}
                {contract.signed_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Assinado em</span>
                    <span>{format(new Date(contract.signed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</span>
                  </div>
                )}
              </div>

              <Separator />

              {/* Signatários */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Signatários ({contract.contract_signatures?.length || 0})
                  </h4>
                  {/* Botão adicionar signatário - apenas para generated/sent + canEdit */}
                  {canEdit && ['generated', 'sent'].includes(contract.status as string) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setShowAddSigner(true)}
                      title="Adicionar signatário"
                    >
                      <UserPlus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {contract.contract_signatures && contract.contract_signatures.length > 0 ? (
                  <SignersList
                    signers={contract.contract_signatures as Array<{
                      id: string;
                      signer_name: string;
                      signer_email: string | null;
                      signer_phone: string | null;
                      signer_role: string;
                      status: SignatureStatus;
                      signed_at: string | null;
                      sign_order: number;
                      provider_data: Record<string, unknown> | null;
                    }>}
                    contractId={contract.id}
                    tenantId={contract.tenant_id}
                    contractStatus={contract.status as ContractStatus}
                    canEdit={canEdit}
                    allowedDeliveryMethods={(contract as any).published_contracts?.allowed_delivery_methods}
                  />
                ) : (
                  <p className="text-xs text-muted-foreground">Nenhum signatário definido</p>
                )}
              </div>

              <Separator />

              {/* v2026-08: Veículos congelados no contrato (snapshot imutável) */}
              <ContractVehiclesSection contractId={contract.id} />

              {/* Timeline de eventos */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Histórico
                </h4>
                <ContractTimeline contractId={contract.id} />
              </div>

              <Separator />

              {/* Botão para abrir preview do contrato em modal centralizado */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowPreview(true)}
              >
                <Eye className="mr-2 h-4 w-4" />
                Visualizar Contrato
              </Button>
            </div>
          </ScrollArea>
        ) : null}
      </SheetContent>

      {/* Modal centralizado para preview fiel ao PDF (A4, 96 DPI, paginação, fundo) */}
      {contract && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>{contract.title} — Preview</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-hidden">
              {/* Escolhe o renderizador conforme o tipo do modelo:
                  - 'uploaded_pdf' (ou HTML vazio) → renderiza o PDF final via iframe
                  - caso contrário → renderiza o HTML paginado padrão */}
              {(((contract as any)?.contract_templates?.source_type === 'uploaded_pdf') ||
                (!contract.content_html && (contract as any)?.pdf_storage_path)) ? (
                <div className="h-full w-full overflow-auto bg-[#e5e5e5] flex justify-center p-4">
                  {contractPdfUrl ? (
                    <ContractPdfPagePreview
                      url={contractPdfUrl}
                      label="Contrato (PDF)"
                      scale={0.8}
                      visualPages={1}
                    />
                  ) : (
                    <div className="flex items-center justify-center text-sm text-muted-foreground">
                      Carregando PDF do contrato…
                    </div>
                  )}
                </div>
              ) : (
                <ContractTemplatePreview
                  contentHtml={contract.content_html}
                  backgroundPreviewUrl={backgroundPreviewUrl}
                  showPageNumbers={(contract as any).contract_templates?.show_page_numbers}
                  pageNumberPosition={(contract as any).contract_templates?.page_number_position}
                  bgMarginTopMm={(contract as any).contract_templates?.bg_margin_top_mm}
                  bgMarginBottomMm={(contract as any).contract_templates?.bg_margin_bottom_mm}
                  paginationYOffsetMm={(contract as any).contract_templates?.pagination_y_offset_mm}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog para adicionar signatário */}
      {contract && (
        <AddSignerDialogWrapper
          open={showAddSigner}
          onOpenChange={setShowAddSigner}
          contractId={contract.id}
          tenantId={contract.tenant_id}
          allowedDeliveryMethods={(contract as any).published_contracts?.allowed_delivery_methods}
        />
      )}
    </Sheet>
  );
}

/**
 * ContractVehiclesSection - Lista os veículos congelados no contrato.
 * Os dados vêm do snapshot gravado em contract_vehicles no momento da geração,
 * garantindo que alterações posteriores no cadastro não afetem o contrato.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-28 v1.1.0 - Só exibe o bloco quando o módulo Veículos está
 *          habilitado no tenant (flag `vehicles_enabled`)
 * RELEASE: 2026-08-09 v1.0.0 - Criação inicial (Fase 7 do módulo de Veículos)
 */
function ContractVehiclesSection({ contractId }: { contractId: string }) {
  // v1.1.0: módulo Veículos desligado no tenant => nada é exibido
  const vehiclesModuleEnabled = useVehiclesModuleEnabled();
  const { vehicles, isLoading } = useContractVehicles(contractId);

  // Sem veículos (ou módulo desligado): não ocupa espaço no painel
  if (!vehiclesModuleEnabled) return null;

  if (isLoading || vehicles.length === 0) return null;

  return (
    <>
      <div className="space-y-3">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <Car className="h-4 w-4" />
          Veículos ({vehicles.length})
        </h4>
        <div className="space-y-2">
          {vehicles.map((v) => (
            <div key={String(v.id)} className="rounded-lg border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm font-medium">
                  {(v.plate as string) || 'Sem placa'}
                </span>
                {v.fipe_value ? (
                  <span className="text-xs text-muted-foreground">
                    {Number(v.fipe_value).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </span>
                ) : null}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {[v.brand_name, v.model_name, v.year, v.color].filter(Boolean).join(' · ') ||
                  'Sem detalhes registrados'}
              </p>
            </div>
          ))}
        </div>
      </div>

      <Separator />
    </>
  );
}
